/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-02 16:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import Classnames from 'classnames';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Icon, TextStyle, Link, FormLayout, Button } from '@shopify/polaris';
import { Field } from 'redux-form';
import { ExternalMinor } from '@shopify/polaris-icons';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderTooltip } from 'src/components/views/common/render-tooltip';

import {
  SHOW_BUTTON_OPTIONS,
  ICON_POSITION_OPTIONS,
  DISPLAY_TYPE_OPTIONS,
  SELECT_OPTIONS_ACTION_OPTIONS,
  DISPLAY_TYPE_VALUE,
  SELECT_OPTIONS_ACTION_VALUE,
  ELEMENTS,
  ELEMENT_KEY_NAME,
  BUTTON_DISPLAY_OPTIONS,
  BUTTON_SHAPE_VALUE,
  SUB_LAYOUT_VALUE,
  CALL_TO_ACTION_POSITION
} from 'src/constants/theme-settings';
import {
  BUTTON_SHAPE_OPTIONS,
  MENU_ID
  // SUB_LAYOUT_OPTIONS
} from 'src/constants/theme-settings.jsx';
import { DropdownIndicator, styles } from './react-select-style';
import { URL } from 'src/constants/links';
import { capitalizeFirstLetter } from 'src/helpers';
import Layout1 from 'src/assets/images/shopify-integration-settings/layout-1.png';
import Layout2 from 'src/assets/images/shopify-integration-settings/layout-2.png';

type Props = {
  formValues: any,
  themeSettingDefaultValue: any,
  themeSettingDefaultThemeValue: any,
  labelTranslation: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class ProductItemCallToActionForm extends Component<ComposedProps> {
  constructor(props) {
    super(props);
    const { i18n } = props;
    this.state = {};
    this.mappingSubLayoutLabel = {
      [SUB_LAYOUT_VALUE.SUB_LAYOUT_1_2]: {
        label: i18n.translate(
          'AdminHTML.ShopifyIntegrationSettings.Form.productCallToAction_Layout1'
        ),
        sample: Layout1
      },
      [SUB_LAYOUT_VALUE.SUB_LAYOUT_2_2]: {
        label: i18n.translate(
          'AdminHTML.ShopifyIntegrationSettings.Form.productCallToAction_Layout2'
        ),
        sample: Layout2
      }
    };
  }

  componentWillReceiveProps(nextProps) {
    const { formValues } = this.props;
    const { formValues: nextFormValues, updateField, themeSettingDefaultValue } = nextProps;
    const keyFormValues = Object.keys(_.get(nextFormValues, 'setting', {}));
    const keyAddToCartBtn = keyFormValues.find(key =>
      key.includes(ELEMENT_KEY_NAME.ADD_TO_CART_BUTTON)
    );
    const keyQuickViewBtn = keyFormValues.find(key =>
      key.includes(ELEMENT_KEY_NAME.QUICK_VIEW_BUTTON)
    );

    const subLayout = _.get(formValues, `setting.${MENU_ID.PRODUCT_ITEM}.subLayout`);
    const nextSubLayout = _.get(nextFormValues, `setting.${MENU_ID.PRODUCT_ITEM}.subLayout`);

    if (subLayout !== nextSubLayout) {
      const setting = _.cloneDeep(nextFormValues?.setting);
      const colors = _.cloneDeep(nextFormValues?.colors);
      const typography = _.cloneDeep(nextFormValues?.typography);
      const addToCartBtn = _.get(setting, keyAddToCartBtn, {});
      const quickViewBtn = _.get(setting, keyQuickViewBtn, {});
      const styleFields = [
        'isEnable',
        'textTransform',
        'backgroundColor',
        'backgroundColorOnHover',
        'borderColor',
        'borderColorOnHover',
        'textColor',
        'textColorOnHover',
        'fontSizeScale',
        'fontFamily',
        'fontStyle'
      ];
      const addToCartBtnStyle = Object.keys(addToCartBtn)
        .filter(key => {
          return styleFields.includes(key);
        })
        .reduce((cur, next) => {
          return {
            ...cur,
            [next]: addToCartBtn[next]
          };
        }, {});
      const quickViewBtnStyle = Object.keys(quickViewBtn)
        .filter(key => {
          return styleFields.includes(key);
        })
        .reduce((cur, next) => {
          return {
            ...cur,
            [next]: quickViewBtn[next]
          };
        }, {});

      // Remove current element position
      delete setting[keyAddToCartBtn];
      delete setting[keyQuickViewBtn];

      const { callToActionLayout } = themeSettingDefaultValue;
      const productItemSubLayout = _.get(setting, `${MENU_ID.PRODUCT_ITEM}.subLayout`);

      if (callToActionLayout[productItemSubLayout]) {
        // Ex: callToActionLayout { subLayout_1_2 : {productInformation_selectOptionBtn:{}}}
        Object.keys(callToActionLayout[productItemSubLayout]).forEach(key => {
          if (key.includes(ELEMENT_KEY_NAME.ADD_TO_CART_BUTTON)) {
            _.set(setting, key, {
              ...callToActionLayout[productItemSubLayout][key],
              ...addToCartBtnStyle
            });
            this.handleUpdateColorRespectiveKey(keyAddToCartBtn, key, colors);
            this.handleUpdateTypographyRespectiveKey(keyAddToCartBtn, key, typography);
          } else {
            _.set(setting, key, {
              ...callToActionLayout[productItemSubLayout][key],
              ...quickViewBtnStyle
            });
            this.handleUpdateColorRespectiveKey(keyQuickViewBtn, key, colors);
            this.handleUpdateTypographyRespectiveKey(keyQuickViewBtn, key, typography);
          }
        });

        updateField('setting', setting);
        updateField('colors', colors);
        updateField('typography', typography);
      }

      return;
    }

    // Add to cart button
    const displayTypeAddToCartBtn = _.get(
      nextFormValues,
      `setting.${keyAddToCartBtn}.displayType`,
      ''
    );
    const buttonShapeAddToCartBtn = _.get(nextFormValues, `setting.${keyAddToCartBtn}.shape`, '');

    if (
      [DISPLAY_TYPE_VALUE.TEXT_ICON, DISPLAY_TYPE_VALUE.TEXT_ONLY].includes(
        displayTypeAddToCartBtn
      ) &&
      [BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(buttonShapeAddToCartBtn)
    ) {
      updateField(`setting.${keyAddToCartBtn}.shape`, BUTTON_SHAPE_VALUE.RECTANGLE);
    }

    if (
      displayTypeAddToCartBtn === DISPLAY_TYPE_VALUE.ICON_ONLY &&
      ![BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(buttonShapeAddToCartBtn)
    ) {
      updateField(`setting.${keyAddToCartBtn}.shape`, BUTTON_SHAPE_VALUE.CIRCLE);
    }

    // Quick view button
    const displayTypeQuickViewBtn = _.get(
      nextFormValues,
      `setting.${keyQuickViewBtn}.displayType`,
      ''
    );
    const buttonShapeQuickViewBtn = _.get(nextFormValues, `setting.${keyQuickViewBtn}.shape`, '');

    if (
      [DISPLAY_TYPE_VALUE.TEXT_ICON, DISPLAY_TYPE_VALUE.TEXT_ONLY].includes(
        displayTypeQuickViewBtn
      ) &&
      [BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(buttonShapeQuickViewBtn)
    ) {
      updateField(`setting.${keyQuickViewBtn}.shape`, BUTTON_SHAPE_VALUE.RECTANGLE);
    }

    if (
      displayTypeQuickViewBtn === DISPLAY_TYPE_VALUE.ICON_ONLY &&
      ![BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(buttonShapeQuickViewBtn)
    ) {
      updateField(`setting.${keyQuickViewBtn}.shape`, BUTTON_SHAPE_VALUE.CIRCLE);
    }
  }

  handleUpdateColorRespectiveKey = (oldKey, newKey, colors) => {
    const advancedSettings = _.get(colors, 'advancedSettings');
    Object.keys(advancedSettings).forEach(key => {
      const advancedSettingsItem = advancedSettings[key];
      Object.keys(advancedSettingsItem).forEach(subKey => {
        const subSettingsItem = advancedSettingsItem[subKey];
        const respectiveKeys = subSettingsItem.respectiveElementKeys || [];
        for (let i = 0; i < respectiveKeys.length; ) {
          const [keyPath, field] = respectiveKeys[i].split('.');
          if (keyPath === oldKey) {
            respectiveKeys.splice(i, 1);
            respectiveKeys.push(`${newKey}.${field}`);
          } else {
            i++;
          }
        }
      });
    });
  };

  handleUpdateTypographyRespectiveKey = (oldKey, newKey, typography) => {
    Object.keys(typography).forEach(key => {
      const typographySettingsItem = typography[key];
      Object.keys(typographySettingsItem).forEach(subKey => {
        if (
          [
            'respectiveElementFontFamilyKeys',
            'respectiveElementFontStyleKeys',
            'respectiveElementFontSizeScaleKeys',
            'respectiveElementTextTransformKeys'
          ].includes(subKey)
        ) {
          const respectiveKeys = typographySettingsItem[subKey] || [];
          for (let i = 0; i < respectiveKeys.length; ) {
            const [keyPath, field] = respectiveKeys[i].split('.');
            if (keyPath === oldKey) {
              respectiveKeys.splice(i, 1);
              respectiveKeys.push(`${newKey}.${field}`);
            } else {
              i++;
            }
          }
        }
      });
    });
  };

  handleChangeButtonPosition = () => {
    const { formValues, updateField } = this.props;
    const clonedFormValues = _.cloneDeep(formValues?.setting);

    const keyFormValues = Object.keys(clonedFormValues);
    const keyAddToCartBtn = keyFormValues.find(key =>
      key.includes(ELEMENT_KEY_NAME.ADD_TO_CART_BUTTON)
    );
    const keyQuickViewBtn = keyFormValues.find(key =>
      key.includes(ELEMENT_KEY_NAME.QUICK_VIEW_BUTTON)
    );

    const productAddToCartBtn = _.get(clonedFormValues, `${keyAddToCartBtn}`);
    const productQuickViewBtn = _.get(clonedFormValues, `${keyQuickViewBtn}`);
    const addToCartBtnPosition = productQuickViewBtn.position;
    const quickViewBtnPosition = productAddToCartBtn.position;

    _.set(clonedFormValues, `${keyAddToCartBtn}.position`, addToCartBtnPosition);
    _.set(clonedFormValues, `${keyQuickViewBtn}.position`, quickViewBtnPosition);
    updateField('setting', clonedFormValues);
  };

  handleOpenListLayout = () => {
    const { updateFieldReducer } = this.props;
    updateFieldReducer('openListLayout', true);
  };

  getButtonShapeOptions = keyBtn => {
    const { formValues } = this.props;
    const displayType = _.get(formValues, `setting.${keyBtn}.displayType`, '');

    const filteredButtonShape = BUTTON_SHAPE_OPTIONS.filter(item => {
      // Text + icon -> remove square and circle
      if (
        [DISPLAY_TYPE_VALUE.TEXT_ICON, DISPLAY_TYPE_VALUE.TEXT_ONLY].includes(displayType) &&
        [BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(item.value)
      ) {
        return false;
      }

      // Icon only -> only shape square and circle
      if (
        displayType === DISPLAY_TYPE_VALUE.ICON_ONLY &&
        ![BUTTON_SHAPE_VALUE.CIRCLE, BUTTON_SHAPE_VALUE.SQUARE].includes(item.value)
      ) {
        return false;
      }
      return true;
    });

    return filteredButtonShape;
  };

  render() {
    const { i18n, prefix, formValues } = this.props;

    const formattedShowButton = SHOW_BUTTON_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedDisplayType = DISPLAY_TYPE_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedSelectOptionAction = SELECT_OPTIONS_ACTION_OPTIONS.filter(item => {
      if (
        [ELEMENTS.PRODUCT_INFORMATION_BUTTON_1, ELEMENTS.PRODUCT_INFORMATION_BUTTON_2].includes(
          prefix
        )
      ) {
        return item.value !== SELECT_OPTIONS_ACTION_VALUE.QUICK_ADD_TO_CART;
      }
      return true;
    }).map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedIconPosition = ICON_POSITION_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedButtonDisplay = BUTTON_DISPLAY_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    // const formattedSubLayout = SUB_LAYOUT_OPTIONS.map(item => ({
    //   ...item,
    //   label: i18n.translate(item.label)
    // }));

    const formattedButtonPosition = CALL_TO_ACTION_POSITION.map(item => ({
      ...item,
      label: capitalizeFirstLetter(i18n.translate(item.label))
    }));

    const keyFormValues = Object.keys(_.get(formValues, 'setting', {}));
    const keyAddToCartBtn = keyFormValues.find(key =>
      key.includes(ELEMENT_KEY_NAME.ADD_TO_CART_BUTTON)
    );
    const keyQuickViewBtn = keyFormValues.find(key =>
      key.includes(ELEMENT_KEY_NAME.QUICK_VIEW_BUTTON)
    );
    const productItemSubLayout = _.get(formValues, `setting.${MENU_ID.PRODUCT_ITEM}.subLayout`);

    const isEnableAddToCartBtn = _.get(formValues, `setting.${keyAddToCartBtn}.isEnable`);
    const isEnableQuickViewBtn = _.get(formValues, `setting.${keyQuickViewBtn}.isEnable`);
    const displayTypeAddToCartBtn = _.get(formValues, `setting.${keyAddToCartBtn}.displayType`, '');
    const displayTypeQuickViewBtn = _.get(formValues, `setting.${keyQuickViewBtn}.displayType`, '');

    const filteredButtonShapeAddToCart = this.getButtonShapeOptions(keyAddToCartBtn);
    const filteredButtonShapeQuickView = this.getButtonShapeOptions(keyQuickViewBtn);

    return (
      <FormLayout>
        <div className="form-section">
          <div>
            <span className="form-link">
              <TextStyle variation="subdued">
                {i18n.translate(
                  'AdminHTML.ShopifyIntegrationSettings.Form.productCallToAction_UserGuide'
                )}
              </TextStyle>{' '}
              <Link url={URL['ProductButtonThemeSetting']} external>
                {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
                <Icon source={ExternalMinor} color="base" />
              </Link>
              <span style={{ color: '#2C6ECB' }}>.</span>
            </span>
          </div>

          <div
            className="form-section__item form-section__item--standard-spacing"
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div className="title" style={{ margin: 0 }}>
              <span>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegrationSettings.Form.productCallToAction_ChooseLayout'
                )}
              </span>
            </div>
            <div className="choose-layout-wrapper">
              <div className="choose-layout-wrapper__layout">
                {this.mappingSubLayoutLabel[productItemSubLayout].label}
                <RenderTooltip
                  content={
                    <img src={this.mappingSubLayoutLabel[productItemSubLayout].sample} alt="" />
                  }
                  icon={true}
                  place="right"
                />
              </div>
              <Button onClick={this.handleOpenListLayout}>
                {i18n.translate(
                  'AdminHTML.ShopifyIntegrationSettings.Form.productCallToAction_ChangeLayout'
                )}
              </Button>
            </div>

            {/* {formattedSubLayout.map((option, index) => {
              const { label, value } = option;
              return (
                <div key={index} style={{ display: 'flex', gap: '8px' }}>
                  <Field
                    name={`setting.${MENU_ID.PRODUCT_ITEM}.subLayout`}
                    id={value}
                    component={RenderRadioButtonItem}
                    label={label}
                  />
                  <RenderTooltip
                    content={<img src={option.sample} alt="" />}
                    icon={true}
                    place="right"
                  />
                </div>
              );
            })} */}
          </div>

          <div className="form-section__item form-section__item--standard-spacing form-section__item--bordered-top">
            <Field
              name={`setting.${keyAddToCartBtn}.isEnable`}
              component={RenderToggleSwitch}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productAddToCartBtn_Enable'
              )}
            />
          </div>

          {isEnableAddToCartBtn && (
            <>
              {/* Show button */}
              <div className="form-section__item form-section__item--standard-spacing">
                <Field
                  name={`setting.${keyAddToCartBtn}.showOn`}
                  component={RenderSelect2}
                  options={formattedShowButton}
                  label={i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Show_Item')}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
              </div>

              {/* Display options */}
              <div className="form-section__item form-section__item--standard-spacing">
                <Field
                  name={`setting.${keyAddToCartBtn}.showOnHovering`}
                  component={RenderSelect2}
                  options={formattedButtonDisplay}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productButton_DisplayOption_Title'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
              </div>

              {/* "Select options" action onclick */}
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${keyAddToCartBtn}.action`}
                  component={RenderSelect2}
                  options={formattedSelectOptionAction}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productAddToCartBtn_SelectAction_Title'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />

                <div style={{ marginTop: '4px' }}>
                  <TextStyle variation="subdued">
                    {i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_SelectOptionAction_Note'
                    )}
                  </TextStyle>
                </div>
              </div>

              {/* Display Type */}
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`setting.${keyAddToCartBtn}.displayType`}
                  component={RenderSelect2}
                  options={formattedDisplayType}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productButton_ButtonStyle_Title'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
                {displayTypeAddToCartBtn === DISPLAY_TYPE_VALUE.TEXT_ICON && (
                  <div style={{ marginTop: '4px' }}>
                    <TextStyle variation="subdued">
                      {i18n.translate(
                        'AdminHTML.ShopifyIntegrationSettings.Form.productButton_ButtonStyle_Note'
                      )}
                    </TextStyle>
                  </div>
                )}
              </div>

              {/* Icon position */}
              {displayTypeAddToCartBtn &&
                displayTypeAddToCartBtn === DISPLAY_TYPE_VALUE.TEXT_ICON && (
                  <div className="form-section__item form-section__item--medium-spacing">
                    <div className="title">
                      <span>
                        {i18n.translate(
                          'AdminHTML.ThemeSetting.Form.button_Overall_IconPosition_Item'
                        )}
                      </span>
                    </div>
                    <div className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--bordered">
                      {formattedIconPosition.map((item, index) => (
                        <Field
                          key={index}
                          name={`setting.${keyAddToCartBtn}.iconPosition`}
                          id={item.value}
                          component={RenderRadioButton2}
                          label={item.label}
                        />
                      ))}
                    </div>
                  </div>
                )}

              {/* Button Shape */}
              {productItemSubLayout !== SUB_LAYOUT_VALUE.SUB_LAYOUT_2_2 && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <p className="title">
                    {i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Shape_Item')}
                  </p>
                  <div
                    className={Classnames(
                      'radio-button-wrapper radio-button-wrapper--outline radio-button-wrapper--no-wrap radio-button-wrapper--full-width',
                      {
                        'radio-button-wrapper--medium':
                          displayTypeAddToCartBtn !== DISPLAY_TYPE_VALUE.ICON_ONLY,
                        'radio-button-wrapper--large':
                          displayTypeAddToCartBtn === DISPLAY_TYPE_VALUE.ICON_ONLY
                      }
                    )}>
                    {filteredButtonShapeAddToCart.map((item, index) => (
                      <Field
                        key={index}
                        name={`setting.${keyAddToCartBtn}.shape`}
                        id={item.value}
                        component={RenderRadioButton2}
                        label={item.icon}
                        fullSize={item.fullSize}
                        isShowIconCheck
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Button position */}
              {isEnableQuickViewBtn && productItemSubLayout === SUB_LAYOUT_VALUE.SUB_LAYOUT_1_2 && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <div className="title">
                    <span>
                      {i18n.translate(
                        'AdminHTML.ShopifyIntegrationSettings.Form.productButton_ButtonPosition_Title'
                      )}
                    </span>
                  </div>
                  <div className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--bordered">
                    {formattedButtonPosition.map((item, index) => (
                      <Field
                        key={index}
                        name={`setting.${keyAddToCartBtn}.buttonPosition`}
                        id={item.value}
                        component={RenderRadioButton2}
                        label={item.label}
                        onChange={this.handleChangeButtonPosition}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="form-section__item form-section__item--standard-spacing form-section__item--bordered-top">
            <Field
              name={`setting.${keyQuickViewBtn}.isEnable`}
              component={RenderToggleSwitch}
              label={i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productQuickViewBtn_Enable'
              )}
            />
          </div>

          {isEnableQuickViewBtn && (
            <>
              {/* Show button */}
              <div className="form-section__item form-section__item--standard-spacing">
                <Field
                  name={`setting.${keyQuickViewBtn}.showOn`}
                  component={RenderSelect2}
                  options={formattedShowButton}
                  label={i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Show_Item')}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
              </div>

              {/* Display options */}
              <div className="form-section__item form-section__item--standard-spacing">
                <Field
                  name={`setting.${keyQuickViewBtn}.showOnHovering`}
                  component={RenderSelect2}
                  options={formattedButtonDisplay}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Form.productButton_DisplayOption_Title'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />
              </div>

              {/* Display Type */}
              {productItemSubLayout !== SUB_LAYOUT_VALUE.SUB_LAYOUT_2_2 && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <Field
                    name={`setting.${keyQuickViewBtn}.displayType`}
                    component={RenderSelect2}
                    options={formattedDisplayType}
                    label={i18n.translate(
                      'AdminHTML.ShopifyIntegrationSettings.Form.productButton_ButtonStyle_Title'
                    )}
                    components={{ DropdownIndicator }}
                    styles={styles}
                    isSearchable={false}
                  />
                  {displayTypeQuickViewBtn === DISPLAY_TYPE_VALUE.TEXT_ICON && (
                    <div style={{ marginTop: '4px' }}>
                      <TextStyle variation="subdued">
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegrationSettings.Form.productButton_ButtonStyle_Note'
                        )}
                      </TextStyle>
                    </div>
                  )}
                </div>
              )}

              {/* Icon position */}
              {displayTypeQuickViewBtn &&
                displayTypeQuickViewBtn === DISPLAY_TYPE_VALUE.TEXT_ICON && (
                  <div className="form-section__item form-section__item--medium-spacing">
                    <div className="title">
                      <span>
                        {i18n.translate(
                          'AdminHTML.ThemeSetting.Form.button_Overall_IconPosition_Item'
                        )}
                      </span>
                    </div>
                    <div className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--bordered">
                      {formattedIconPosition.map((item, index) => (
                        <Field
                          key={index}
                          name={`setting.${keyQuickViewBtn}.iconPosition`}
                          id={item.value}
                          component={RenderRadioButton2}
                          label={item.label}
                        />
                      ))}
                    </div>
                  </div>
                )}

              {/* Button Shape */}
              {productItemSubLayout !== SUB_LAYOUT_VALUE.SUB_LAYOUT_2_2 && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <p className="title">
                    {i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Shape_Item')}
                  </p>
                  <div
                    className={Classnames(
                      'radio-button-wrapper radio-button-wrapper--outline radio-button-wrapper--no-wrap',
                      {
                        'radio-button-wrapper--medium':
                          displayTypeQuickViewBtn !== DISPLAY_TYPE_VALUE.ICON_ONLY,
                        'radio-button-wrapper--large':
                          displayTypeQuickViewBtn === DISPLAY_TYPE_VALUE.ICON_ONLY
                      }
                    )}>
                    {filteredButtonShapeQuickView.map((item, index) => (
                      <Field
                        key={index}
                        name={`setting.${keyQuickViewBtn}.shape`}
                        id={item.value}
                        component={RenderRadioButton2}
                        label={item.icon}
                        fullSize={item.fullSize}
                        isShowIconCheck
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(ProductItemCallToActionForm);
